import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Send, Activity, CheckCircle2, XCircle, Clock, Webhook } from "lucide-react";
import api, { getToken, getLevel } from "@/lib/api";
import { toast } from "sonner";

const KNOWN_EVENTS = [
  { id: "season.opened", label: "Season Opened (admin activated a new season)" },
  { id: "season.archived", label: "Season Archived (admin archived a season)" },
  { id: "*", label: "All events (wildcard)" },
];

function CreateSubModal({ onClose, onCreated }) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState(["season.archived", "season.opened"]);
  const [secret, setSecret] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setEvents((cur) => cur.includes(id)
    ? cur.filter((x) => x !== id) : [...cur, id]);

  const submit = async () => {
    if (!url.trim()) { toast.error("URL is required"); return; }
    if (!url.match(/^https?:\/\//)) { toast.error("URL must start with http:// or https://"); return; }
    if (events.length === 0) { toast.error("Select at least one event"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/webhooks/subscriptions", {
        url: url.trim(), events, secret: secret.trim() || null,
        description: description.trim() || null,
      });
      toast.success("Webhook subscription created.");
      onCreated(r.data.subscription);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="webhook-create-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-3">Add Webhook Subscription</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Webhook URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/hooks/rrdc"
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono" data-testid="webhook-url" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Events</label>
            <div className="space-y-1.5">
              {KNOWN_EVENTS.map((ev) => (
                <label key={ev.id} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={events.includes(ev.id)} onChange={() => toggle(ev.id)}
                         className="mt-0.5" data-testid={`webhook-event-${ev.id}`} />
                  <span><span className="font-mono text-xs bg-slate-100 px-1 rounded">{ev.id}</span> <span className="text-slate-600">{ev.label}</span></span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Signing Secret (optional)</label>
            <input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Used to sign payloads (HMAC-SHA256)"
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono" data-testid="webhook-secret" />
            <div className="text-[11px] text-slate-500 mt-1">When set, each delivery includes header <code className="bg-slate-100 px-1 rounded">X-RRDC-Signature: sha256=…</code></div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Operations Slack channel"
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="webhook-desc" />
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1"
                  data-testid="webhook-create-submit">
            <Plus className="w-4 h-4" /> {busy ? "Creating…" : "Add Subscription"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WebhooksPage() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testingId, setTestingId] = useState(null);

  useEffect(() => {
    if (!getToken()) { navigate("/"); return; }
    if (getLevel() !== "leadership") {
      toast.error("Webhooks are leadership-only.");
      navigate("/settings");
    }
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/admin/webhooks/subscriptions"),
        api.get("/admin/webhooks/events?limit=20"),
      ]);
      setSubs(s.data.items || []);
      setEvents(e.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load webhooks");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this webhook subscription?")) return;
    try {
      await api.delete(`/admin/webhooks/subscriptions/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const test = async (id) => {
    setTestingId(id);
    try {
      const r = await api.post(`/admin/webhooks/subscriptions/${id}/test`);
      const res = r.data?.result || {};
      if (res.status_code && res.status_code < 400) {
        toast.success(`Delivered (HTTP ${res.status_code})`);
      } else {
        toast.error(`Failed: ${res.error || "no response"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTestingId(null); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/settings")} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 font-semibold text-sm" data-testid="back-to-settings">
            <ArrowLeft className="w-4 h-4" /> Settings
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rolling River</div>
            <h1 className="font-display text-lg sm:text-xl font-bold text-slate-900 inline-flex items-center gap-2">
              <Webhook className="w-5 h-5 text-blue-600" /> Webhooks & Events
            </h1>
          </div>
          <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold" data-testid="add-subscription">
            <Plus className="w-4 h-4" /> Add Subscription
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-5 space-y-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="subs-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-base text-slate-900">Subscriptions <span className="text-slate-400 font-normal text-sm">({subs.length})</span></h2>
          </div>
          {loading ? (
            <div className="py-6 text-center text-slate-400 text-sm">Loading…</div>
          ) : subs.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              No webhook subscriptions yet. Click <strong className="text-slate-700">Add Subscription</strong> to receive events from Staff Snapshot.
            </div>
          ) : (
            <div className="space-y-2">
              {subs.map((s) => (
                <div key={s._id} className="rounded-xl border border-slate-200 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`sub-${s._id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] ${s.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                        {s.active ? "Active" : "Disabled"}
                      </span>
                      <span className="font-mono text-sm text-slate-900 break-all">{s.url}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{s.description || "—"}</div>
                    <div className="text-xs text-slate-600 mt-1 flex items-center gap-1.5 flex-wrap">
                      Events:
                      {(s.events || []).map((ev) => (
                        <span key={ev} className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{ev}</span>
                      ))}
                      {s.secret_present && (
                        <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold uppercase tracking-[0.1em]">Signed ·{s.secret_hint}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => test(s._id)} disabled={testingId === s._id} className="px-2.5 py-1.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid={`test-${s._id}`}>
                      <Send className="w-3.5 h-3.5" /> {testingId === s._id ? "Sending…" : "Test"}
                    </button>
                    <button onClick={() => remove(s._id)} className="px-2.5 py-1.5 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-800 text-xs font-semibold inline-flex items-center gap-1" data-testid={`delete-${s._id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="events-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-base text-slate-900 inline-flex items-center gap-1.5">
              <Activity className="w-4 h-4 text-slate-500" /> Recent Events <span className="text-slate-400 font-normal text-sm">({events.length})</span>
            </h2>
            <button onClick={load} className="text-xs text-blue-600 hover:underline" data-testid="refresh-events">Refresh</button>
          </div>
          {events.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">No events yet.</div>
          ) : (
            <div className="space-y-2">
              {events.map((ev) => (
                <details key={ev._id} className="rounded-lg border border-slate-200 bg-slate-50/50" data-testid={`event-${ev._id}`}>
                  <summary className="cursor-pointer px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">{ev.type}</span>
                      <span className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {ev.created_at_utc?.slice(0, 19)}Z</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      {(ev.dispatches || []).map((dx, i) => dx.status_code && dx.status_code < 400 ? (
                        <span key={i} className="inline-flex items-center gap-0.5 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> {dx.status_code}</span>
                      ) : (
                        <span key={i} className="inline-flex items-center gap-0.5 text-rose-700"><XCircle className="w-3.5 h-3.5" /> {dx.status_code || "error"}</span>
                      ))}
                      {(!ev.dispatches || ev.dispatches.length === 0) && (
                        <span className="text-slate-400 italic">no subscribers</span>
                      )}
                    </div>
                  </summary>
                  <div className="px-3 pb-3">
                    <pre className="text-[11px] bg-white border border-slate-200 rounded p-2 overflow-x-auto">{JSON.stringify(ev.payload, null, 2)}</pre>
                    {ev.dispatches && ev.dispatches.length > 0 && (
                      <div className="mt-2 text-[11px] text-slate-600 space-y-1">
                        {ev.dispatches.map((dx, i) => (
                          <div key={i} className="font-mono break-all">→ {dx.url}: <span className={dx.status_code && dx.status_code < 400 ? "text-emerald-700" : "text-rose-700"}>{dx.status_code || dx.error}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>
          )}
        </section>
      </main>

      {showCreate && <CreateSubModal onClose={() => setShowCreate(false)} onCreated={() => load()} />}
    </div>
  );
}
